"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Clock, Copy, Check, RefreshCw, ArrowRightLeft } from "lucide-react"
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard"

const commonTimezones = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "New York (EST/EDT)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PST/PDT)" },
  { value: "America/Chicago", label: "Chicago (CST/CDT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "Europe/Kyiv", label: "Kyiv (EET/EEST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
]

function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const absDiff = Math.abs(diff)
  const isPast = diff > 0

  const seconds = Math.floor(absDiff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  let value: number
  let unit: string

  if (years > 0) {
    value = years
    unit = years === 1 ? "year" : "years"
  } else if (months > 0) {
    value = months
    unit = months === 1 ? "month" : "months"
  } else if (days > 0) {
    value = days
    unit = days === 1 ? "day" : "days"
  } else if (hours > 0) {
    value = hours
    unit = hours === 1 ? "hour" : "hours"
  } else if (minutes > 0) {
    value = minutes
    unit = minutes === 1 ? "minute" : "minutes"
  } else {
    value = seconds
    unit = seconds === 1 ? "second" : "seconds"
  }

  return isPast ? `${value} ${unit} ago` : `in ${value} ${unit}`
}

export function TimestampConverter() {
  const [timestamp, setTimestamp] = useState("")
  const [dateString, setDateString] = useState("")
  const [timezone, setTimezone] = useState("UTC")
  const [currentTime, setCurrentTime] = useState<Date>(new Date())
  const { copy, isCopied } = useCopyToClipboard()

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const formatTimestamp = useCallback((value: string, targetTimezone: string) => {
    if (!value.trim()) return ""

    let numericTimestamp = Number.parseInt(value, 10)
    if (Number.isNaN(numericTimestamp)) return "Invalid timestamp"

    if (numericTimestamp < 1e12) {
      numericTimestamp *= 1000
    }

    const date = new Date(numericTimestamp)
    if (Number.isNaN(date.getTime())) return "Invalid timestamp"

    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "full",
      timeStyle: "long",
      timeZone: targetTimezone,
    }).format(date)
  }, [])

  const handleTimestampChange = (value: string) => {
    setTimestamp(value)
    setDateString(formatTimestamp(value, timezone))
  }

  const parseDate = useCallback((value: string) => {
    setDateString(value)
    if (!value.trim()) {
      setTimestamp("")
      return
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      setTimestamp("Invalid date")
      return
    }

    const nextTimestamp = Math.floor(date.getTime() / 1000).toString()
    setTimestamp(nextTimestamp)
    setDateString(formatTimestamp(nextTimestamp, timezone))
  }, [formatTimestamp, timezone])

  const handleTimezoneChange = (value: string) => {
    setTimezone(value)
    setDateString(formatTimestamp(timestamp, value))
  }

  const setNow = () => {
    handleTimestampChange(Math.floor(Date.now() / 1000).toString())
  }

  const copyToClipboard = (text: string, field: string) => {
    copy(text, field)
  }

  const swapValues = () => {
    if (dateString && dateString !== "Invalid timestamp") {
      const date = new Date(dateString)
      if (!Number.isNaN(date.getTime())) {
        setDateString("")
        setTimestamp(Math.floor(date.getTime() / 1000).toString())
      }
    }
  }

  // Format current time in different formats
  const currentFormats = [
    { label: "Unix (s)", value: Math.floor(currentTime.getTime() / 1000).toString() },
    { label: "Unix (ms)", value: currentTime.getTime().toString() },
    { label: "ISO 8601", value: currentTime.toISOString() },
    {
      label: `Local (${timezone})`,
      value: new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "medium",
        timeZone: timezone,
      }).format(currentTime),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Current Time Display */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Current Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {currentFormats.map((format) => (
              <div
                key={format.label}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 group"
              >
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{format.label}</p>
                  <p className="font-mono text-sm truncate">{format.value}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={() => copyToClipboard(format.value, format.label)}
                >
                  {isCopied(format.label) ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Converter */}
      <Card>
        <CardHeader>
          <CardTitle>Convert Timestamp</CardTitle>
          <CardDescription>
            Convert between Unix timestamps and human-readable dates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Timezone selector */}
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select value={timezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger className="w-full sm:w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {commonTimezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-4 items-end">
            {/* Unix Timestamp Input */}
            <div className="space-y-2">
              <Label htmlFor="timestamp">Unix Timestamp</Label>
              <div className="flex gap-2">
                <Input
                  id="timestamp"
                  type="text"
                  placeholder="1705315800"
                  value={timestamp}
                  onChange={(e) => handleTimestampChange(e.target.value)}
                  className="font-mono"
                />
                <Button variant="outline" size="icon" onClick={setNow} title="Set to now">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Seconds or milliseconds (auto-detected)
              </p>
            </div>

            {/* Swap button */}
            <div className="flex justify-center pb-6">
              <Button variant="ghost" size="icon" onClick={swapValues}>
                <ArrowRightLeft className="h-4 w-4" />
              </Button>
            </div>

            {/* Date String Input */}
            <div className="space-y-2">
              <Label htmlFor="datestring">Date & Time</Label>
              <Input
                id="datestring"
                type="text"
                placeholder="2024-01-15T10:30:00Z"
                value={dateString}
                onChange={(e) => parseDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                ISO 8601, RFC 2822, or natural language
              </p>
            </div>
          </div>

          {/* Result Display */}
          {timestamp && dateString && dateString !== "Invalid timestamp" && (
            <div className="p-4 rounded-lg bg-muted/50 space-y-3">
              <h3 className="font-medium">Converted Result</h3>
              <div className="grid gap-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Full Date:</span>
                  <span className="font-mono">{dateString}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Unix (seconds):</span>
                  <span className="font-mono">{timestamp}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Unix (milliseconds):</span>
                  <span className="font-mono">{Number(timestamp) * 1000}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">ISO 8601:</span>
                  <span className="font-mono">
                    {new Date(Number(timestamp) * 1000).toISOString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Relative:</span>
                  <span>{formatRelativeTime(new Date(Number(timestamp) * 1000))}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Common Timestamps</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>0 = Jan 1, 1970 (Unix Epoch)</li>
                <li>1000000000 = Sep 9, 2001</li>
                <li>1500000000 = Jul 14, 2017</li>
                <li>2000000000 = May 18, 2033</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Date Formats</h4>
              <ul className="space-y-1 text-muted-foreground font-mono text-xs">
                <li>2024-01-15T10:30:00Z</li>
                <li>2024-01-15 10:30:00</li>
                <li>Jan 15, 2024 10:30 AM</li>
                <li>15/01/2024 10:30</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Time Units</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>1 minute = 60 seconds</li>
                <li>1 hour = 3,600 seconds</li>
                <li>1 day = 86,400 seconds</li>
                <li>1 week = 604,800 seconds</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
